import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { ROUTES, ROUTE_SEARCH_PARAM } from 'src/constants/route';
import { useListWebinarsForUserQuery } from 'src/redux/slices/api/su';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { WEBINAR_REGISTRATION_STATUS } from 'src/constants/webinar';
import { mergeUrlParameters } from 'src/utils/routing';

// Bi-directional redirect between old and new learn bases.
// Keeps full path + query + hash.
export const BiDirectionalRedirect: React.FC = () => {
  const location = useLocation();
  const fullPath = location.pathname + location.search + location.hash;

  const redirectMap: Record<string, string> = {
    [ROUTES.NEW_LEARN.BASE]: ROUTES.LEARN.BASE,
    [ROUTES.LEARN.BASE]: ROUTES.NEW_LEARN.BASE,
  };

  const matchingRedirect = Object.entries(redirectMap).find(([from]) => location.pathname.startsWith(from));

  if (matchingRedirect) {
    const [from, to] = matchingRedirect;
    return <Navigate to={fullPath.replace(from, to)} replace />;
  }

  return null;
};

// Legacy module redirect
// Handles: /learn/courses?moduleId=...  => /su/learn/learning-module/:id[?originalQuery]
// Appends the entire original query string (including moduleId itself) to the new URL.
export const LegacyModuleRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const legacyId = searchParams.get(ROUTE_SEARCH_PARAM.LEGACY_MODULE_ID);
  const queryString = searchParams.toString();
  const suffix = queryString ? `?${queryString}` : '';

  if (!legacyId) {
    return <Navigate to={`${ROUTES.NEW_LEARN.BASE}${suffix}`} replace />;
  }

  const to = `${ROUTES.NEW_LEARN.LEARNING_MODULE}/${encodeURIComponent(legacyId)}${suffix}`;

  return <Navigate to={to} replace />;
};

// Legacy webinar join page redirect to 3rd party amazon.livewebinar.com/...
// Handles: /learn/newWebinarPage?webinarId=...&. => redirect based on user registration status
export const NewWebinarPageRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [hasExternalRedirect, setHasExternalRedirect] = useState(false);
  const webinarId = searchParams.get('webinarId');
  const queryString = searchParams.toString();
  const suffix = queryString ? `?${queryString}` : '';

  // API query for webinars
  const {
    data: webinarsData,
    isLoading: isWebinarsLoading,
    isError: isWebinarsError,
    error: webinarsErrorObj,
  } = useListWebinarsForUserQuery();

  useEffect(() => {
    // If no webinarId parameter, redirect to SU webinar page
    if (!webinarId) {
      setRedirectTo(`${ROUTES.LEARN.WEBINAR}${suffix}`);
      return;
    }

    // Don't process if still loading
    if (isWebinarsLoading) return;

    // If API error, redirect to SU webinar page and log
    if (isWebinarsError) {
      ClientLogger.error(ClientLog.IllegalInternalDataState, {
        message: 'Failed to make API call to load webinars for user for validation',
        webinarId,
        path: window.location.pathname,
        url: window.location.href,
        error: webinarsErrorObj,
      });
      setRedirectTo(`${ROUTES.LEARN.WEBINAR}${suffix}`);
      return;
    }

    // Find webinar in the response
    if (webinarsData?.webinars) {
      const selectedWebinar = webinarsData.webinars.find(
        (webinar) => webinar.webinarId === webinarId,
      );

      // If webinar not found, redirect to SU webinar page
      if (!selectedWebinar) {
        setRedirectTo(`${ROUTES.LEARN.WEBINAR}${suffix}`);
        return;
      }

      // Check registration status and redirect accordingly
      const isRegistered = selectedWebinar.registrationStatus === WEBINAR_REGISTRATION_STATUS.JOIN;
      if (isRegistered && selectedWebinar.registrantURL) {
        // User is registered - redirect to 3rd party registrant URL with parameters
        const urlWithParams = mergeUrlParameters(selectedWebinar.registrantURL, suffix);
        setHasExternalRedirect(true);
        window.location.href = urlWithParams;
      } else {
        // User not registered or webinar not in join state
        // Redirect to registration modal on SU webinar page
        setRedirectTo(`${ROUTES.LEARN.WEBINAR}${suffix}`);
      }
    }
  }, [webinarId, webinarsData, isWebinarsLoading, isWebinarsError, webinarsErrorObj, suffix]);

  if (hasExternalRedirect) {
    return null;
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return null;
};
